import React, { FC } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    CircularProgress,
    Alert,
    Box,
    Typography,
} from '@mui/material';
import { useDataProvider } from 'react-admin';
import { useQuery } from '@tanstack/react-query';
import { dateTimeFormatter } from './common';

type MailgunEvent = {
    event: string;
    timestamp: number;
    recipient?: string;
    message?: { headers?: { subject?: string; from?: string; to?: string } };
    'delivery-status'?: {
        message?: string;
        description?: string;
        code?: number;
        'attempt-no'?: number;
        'mx-host'?: string;
    };
    severity?: string;
    reason?: string;
};

const eventColorMap: Record<
    string,
    'success' | 'error' | 'warning' | 'info' | 'default'
> = {
    delivered: 'success',
    accepted: 'info',
    failed: 'error',
    rejected: 'error',
    complained: 'warning',
    opened: 'success',
    clicked: 'success',
    unsubscribed: 'warning',
    stored: 'info',
};

const MailgunEventsDialog: FC<{
    open: boolean;
    onClose: () => void;
    mailgunId: string | null;
}> = ({ open, onClose, mailgunId }) => {
    const dataProvider = useDataProvider();

    const params = `filter=${JSON.stringify({ message_id: mailgunId })}`;
    const { data, error, isPending } = useQuery({
        queryKey: ['mailgun-events', mailgunId],
        queryFn: () =>
            dataProvider.getAny('/statistics/mailgun-events', params),
        enabled: open && !!mailgunId,
        staleTime: 60_000,
    });

    const events: MailgunEvent[] = data?.data?.items || [];

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '8px',
                    padding: '8px',
                },
            }}
        >
            <DialogTitle>
                <Typography variant="h6">Mailgun-tapahtumat</Typography>
                {mailgunId && (
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ wordBreak: 'break-all' }}
                    >
                        {mailgunId}
                    </Typography>
                )}
            </DialogTitle>
            <DialogContent>
                {isPending && (
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        minHeight={120}
                    >
                        <CircularProgress />
                    </Box>
                )}
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        Mailgun-tapahtumien haku epäonnistui:{' '}
                        {(error as Error)?.message || 'Tuntematon virhe'}
                    </Alert>
                )}
                {!isPending && !error && events.length === 0 && (
                    <Alert severity="info">
                        Ei Mailgun-tapahtumia tälle viestille.
                    </Alert>
                )}
                {!isPending && events.length > 0 && (
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Aika</TableCell>
                                    <TableCell>Tapahtuma</TableCell>
                                    <TableCell>Vastaanottaja</TableCell>
                                    <TableCell>Tiedot</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {events.map(
                                    (event: MailgunEvent, index: number) => (
                                        <TableRow key={index}>
                                            <TableCell
                                                sx={{ whiteSpace: 'nowrap' }}
                                            >
                                                {dateTimeFormatter.format(
                                                    new Date(
                                                        event.timestamp * 1000
                                                    )
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={event.event}
                                                    color={
                                                        eventColorMap[
                                                            event.event
                                                        ] || 'default'
                                                    }
                                                    size="small"
                                                />
                                                {event.severity &&
                                                    event.severity !==
                                                        'permanent' && (
                                                        <Chip
                                                            label={
                                                                event.severity
                                                            }
                                                            size="small"
                                                            sx={{ ml: 0.5 }}
                                                        />
                                                    )}
                                            </TableCell>
                                            <TableCell>
                                                {event.recipient || '-'}
                                            </TableCell>
                                            <TableCell>
                                                {event['delivery-status']
                                                    ?.message ||
                                                    event['delivery-status']
                                                        ?.description ||
                                                    event.reason ||
                                                    '-'}
                                            </TableCell>
                                        </TableRow>
                                    )
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Sulje</Button>
            </DialogActions>
        </Dialog>
    );
};

export default MailgunEventsDialog;
